'use client';

import React, { type ReactNode } from 'react';
import type { Brand } from '@/domainLogic/index.ts';
import Navigation from '@/app/components/navigation.tsx';
import SearchPageDataProvider from '@/app/components/search/searchPageDataProvider.tsx';
import { CSWAppStickyWrapper, HeaderContainer, TsxMainContent, TsxNavigation } from '@/app/common/styled.ts';
import LoadLine from '@/app/components/Loadline.tsx';
import { StickyNavProvider } from '@/app/common/components/StickyNavBar/StickyNavContext.tsx';
import StickyNavHeader from '@/app/common/components/StickyNavBar/StickyNavHeader.tsx';
import { TopMargin } from '@/app/components/topMargin';
import type { SearchPageData } from '@/app/search/createSearchPageData.ts';
import { PAGE_TYPE } from '@/app/common/pageType.ts';
import { UBUNTU_FONT_STACK } from '@/utils/ubuntuFont';
import HeaderManager, { type HeaderManagerProps } from './HeaderManager';

type NavigationManagerProps = {
  evolutionResultsOnlyEnabled: boolean;
  isApp: boolean;
  appShowCsw: boolean;
  externalHeaderEnabled: boolean;
  showExternalCSW: boolean;
  /** Header scrolls away with the page instead of being fixed/sticky (evo detail steps). */
  staticHeader?: boolean;
  brand: Brand;
  showGlobalLoadLine: boolean;
  showNavigation: boolean;
  pageType: PAGE_TYPE;
  handleGoToNextStep: (pageType: PAGE_TYPE) => void;
  searchPageData: SearchPageData;
  headerProps: HeaderManagerProps;
  children: ReactNode;
};

const NavigationManager = ({
  evolutionResultsOnlyEnabled,
  isApp,
  appShowCsw,
  externalHeaderEnabled,
  showExternalCSW,
  staticHeader,
  brand,
  showGlobalLoadLine,
  showNavigation,
  pageType,
  handleGoToNextStep,
  searchPageData,
  headerProps,
  children,
}: NavigationManagerProps) => {
  const showHeader = !isApp || appShowCsw;
  // Non-evo detail steps: the fixed external header carries the CSW (MPH-864) but has no
  // list/index/map body class, so TopMargin needs an explicit widget-height offset.
  const showExternalCSWDetail =
    showExternalCSW && (pageType === PAGE_TYPE.HOTEL_DETAIL || pageType === PAGE_TYPE.TRANSPORT_DETAIL);
  // MPH-395 workaround (see CSWAppContainer in styled.ts): standalone CSW in the
  // app, LM only — HP/HG (externalHeaderEnabled) keep their external-CSW handling.
  // It occupies flow space, so no fixed HeaderContainer and no TopMargin.
  const showAppCswOnly = isApp && appShowCsw && !externalHeaderEnabled;
  // The relative wrapper anchors the global load line to the CSW's bottom edge.
  const appCswHeader = (
    <div style={{ position: 'relative' }}>
      <HeaderManager {...headerProps} />
      {showGlobalLoadLine && <LoadLine $isGlobal={true} />}
    </div>
  );
  if (evolutionResultsOnlyEnabled) {
    const header = (
      <HeaderContainer
        externalHeaderEnabled={externalHeaderEnabled}
        showExternalCSW={showExternalCSW}
        brand={brand}
        evolutionEnabled={true}
        staticHeader={staticHeader}
      >
        <HeaderManager {...headerProps} />
        {showGlobalLoadLine && <LoadLine $isGlobal={true} style={{ position: 'absolute', marginTop: 64, top: 0 }} />}
      </HeaderContainer>
    );
    return (
      <StickyNavProvider>
        {showHeader &&
          (showAppCswOnly ? (
            // Web parity: slide-out/in on scroll; evo detail steps scroll away.
            staticHeader ? (
              appCswHeader
            ) : (
              <StickyNavHeader>{appCswHeader}</StickyNavHeader>
            )
          ) : staticHeader ? (
            header
          ) : (
            <StickyNavHeader>{header}</StickyNavHeader>
          ))}
        {/* No TopMargin with a static header or the app CSW: both occupy real space in the flow. */}
        {showHeader && !staticHeader && !showAppCswOnly && (
          <TopMargin externalHeaderEnabled={externalHeaderEnabled} brand={brand} evolutionEnabled={true} />
        )}
        {showNavigation && <Navigation currentPage={pageType} handleGoToNextStep={handleGoToNextStep} />}
        <TsxMainContent
          role="region"
          style={{
            background: 'white',
            fontFamily: UBUNTU_FONT_STACK,
          }}
        >
          <SearchPageDataProvider searchPageData={searchPageData}>{children}</SearchPageDataProvider>
        </TsxMainContent>
      </StickyNavProvider>
    );
  }

  return (
    <>
      {/* Web parity: always pinned, like the fixed non-evo header. Must stay
          outside TsxNavigation (see CSWAppStickyWrapper). */}
      {showHeader && showAppCswOnly && <CSWAppStickyWrapper>{appCswHeader}</CSWAppStickyWrapper>}
      <TsxNavigation>
        {showHeader && !showAppCswOnly && (
          <HeaderContainer
            externalHeaderEnabled={externalHeaderEnabled}
            showExternalCSW={showExternalCSW}
            brand={brand}
            evolutionEnabled={false}
          >
            <HeaderManager {...headerProps} />
            {showGlobalLoadLine && <LoadLine $isGlobal={true} style={{ marginTop: 64, top: 0 }} />}
          </HeaderContainer>
        )}
        {showHeader && !showAppCswOnly && (
          <TopMargin
            externalHeaderEnabled={externalHeaderEnabled}
            brand={brand}
            evolutionEnabled={false}
            showExternalCSWDetail={showExternalCSWDetail}
          />
        )}
        {showNavigation && <Navigation currentPage={pageType} handleGoToNextStep={handleGoToNextStep} />}
      </TsxNavigation>
      <TsxMainContent role="region" style={{ fontFamily: UBUNTU_FONT_STACK }}>
        <SearchPageDataProvider searchPageData={searchPageData}>{children}</SearchPageDataProvider>
      </TsxMainContent>
    </>
  );
};

export default NavigationManager;
